import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Users, 
  Activity, 
  Edit2, 
  Save, 
  X, 
  TrendingUp,
  TrendingDown,
  Sparkles,
  CheckCircle,
  Shield,
  Layers,
  Clock,
  Phone,
  Server,
  AlertTriangle
} from 'lucide-react';
import { Customer, ActivityMetric, updateCustomer } from '@/services/customerService';

interface CustomerDetailsSheetProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

export function CustomerDetailsSheet({ customer, isOpen, onClose, onUpdate }: CustomerDetailsSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    description: customer?.description || '',
    customerCount: customer?.customerCount || 0,
    status: customer?.status || 'active',
    activityMetrics: customer?.activityMetrics || [],
  });

  if (!customer) return null;

  // Reset form when customer changes or when starting edit
  const startEdit = () => {
    setFormData({
      name: customer.name,
      description: customer.description || '',
      customerCount: customer.customerCount || 0,
      status: customer.status || 'active',
      activityMetrics: customer.activityMetrics || [],
    });
    setIsEditing(true);
    setError(null);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      await updateCustomer(customer.id, {
        ...customer,
        ...formData,
      });

      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update customer persona');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addMetric = () => {
    const newMetrics = [...formData.activityMetrics];
    newMetrics.push({ name: '', value: 0, unit: 'per day', icon: 'Activity' });
    setFormData({ ...formData, activityMetrics: newMetrics });
  };

  const updateMetric = (index: number, field: keyof ActivityMetric, value: string | number) => {
    const newMetrics = [...formData.activityMetrics];
    const currentMetric = newMetrics[index];
    
    if (!currentMetric) return;
    
    newMetrics[index] = {
      ...currentMetric,
      [field]: value
    } as ActivityMetric;
    
    setFormData({ ...formData, activityMetrics: newMetrics });
  };

  const removeMetric = (index: number) => {
    const newMetrics = formData.activityMetrics.filter((_, i) => i !== index);
    setFormData({ ...formData, activityMetrics: newMetrics });
  };

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'TrendingUp': return TrendingUp;
      case 'TrendingDown': return TrendingDown;
      case 'Users': return Users;
      case 'Sparkles': return Sparkles;
      case 'CheckCircle': return CheckCircle;
      case 'Shield': return Shield;
      case 'Layers': return Layers;
      case 'Clock': return Clock;
      case 'Phone': return Phone;
      case 'Server': return Server;
      case 'AlertTriangle': return AlertTriangle;
      default: return Activity;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {customer.name}
            </div>
            {!isEditing ? (
              <Button
                variant="outline"
                size="sm"
                onClick={startEdit}
                className="text-primary-500 border-primary-500 hover:bg-primary-500/10"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEdit}
                  disabled={isSubmitting}
                  className="text-neutral-400 border-neutral-600 hover:bg-neutral-800"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  className="bg-primary-500 text-black hover:bg-primary-600"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            )}
          </SheetTitle>
          <SheetDescription>Customer persona details and activity metrics</SheetDescription>
        </SheetHeader>

        {error && (
          <div className="mt-4 p-3 bg-red-900/20 border border-red-500/50 rounded-md text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="mt-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Basic Information</h3>
            {!isEditing ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Customer Count:</span>
                  <span className="text-white">{customer.customerCount?.toLocaleString() || '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Activity className="h-4 w-4 text-neutral-400" />
                  <span className="text-neutral-400">Status:</span>
                  <Badge
                    className={
                      customer.status === 'active'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : customer.status === 'inactive'
                        ? 'bg-red-500/20 text-red-400 border-red-500/30'
                        : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                    }
                  >
                    {customer.status}
                  </Badge>
                </div>
                {customer.description && (
                  <div className="text-sm">
                    <span className="text-neutral-400">Description:</span>
                    <p className="text-white mt-1">{customer.description}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Persona Name *</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Input
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-customer-count">Customer Count *</Label>
                  <Input
                    id="edit-customer-count"
                    type="number"
                    value={formData.customerCount}
                    onChange={(e) => handleChange('customerCount', parseInt(e.target.value) || 0)}
                    className="bg-neutral-900 border-neutral-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-status">Status *</Label>
                  <select
                    id="edit-status"
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    className="w-full bg-neutral-900 border-neutral-700 text-white rounded-md px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Activity Metrics */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white">Activity Metrics</h3>
              {isEditing && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addMetric}
                  className="text-primary-500 border-primary-500 hover:bg-primary-500/10"
                >
                  Add Metric
                </Button>
              )}
            </div>
            
            {!isEditing ? (
              <div className="space-y-2">
                {customer.activityMetrics && customer.activityMetrics.length > 0 ? (
                  customer.activityMetrics.map((metric, idx) => {
                    const IconComponent = getIconComponent(metric.icon || 'Activity');
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm bg-neutral-900/50 p-2 rounded">
                        <IconComponent className="h-4 w-4 text-primary-400" />
                        <span className="text-white font-medium">{metric.name}:</span>
                        <span className="text-neutral-300">
                          {metric.value.toLocaleString()}
                          {metric.unit && <span className="text-neutral-500 ml-1">({metric.unit})</span>}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-neutral-400 text-sm">No activity metrics defined</p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {formData.activityMetrics.map((metric, index) => (
                  <div key={index} className="p-3 bg-neutral-900/50 rounded-lg border border-neutral-800 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Metric Name</Label>
                        <Input
                          value={metric.name}
                          onChange={(e) => updateMetric(index, 'name', e.target.value)}
                          placeholder="e.g., Churn Rate"
                          className="bg-neutral-900 border-neutral-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Value</Label>
                        <Input
                          type="number"
                          value={metric.value}
                          onChange={(e) => updateMetric(index, 'value', parseFloat(e.target.value) || 0)}
                          className="bg-neutral-900 border-neutral-700"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Unit</Label>
                        <Input
                          value={metric.unit}
                          onChange={(e) => updateMetric(index, 'unit', e.target.value)}
                          placeholder="e.g., % per month"
                          className="bg-neutral-900 border-neutral-700"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <select
                          value={metric.icon}
                          onChange={(e) => updateMetric(index, 'icon', e.target.value)}
                          className="w-full bg-neutral-900 border-neutral-700 text-white rounded-md px-3 py-2"
                        >
                          <option value="Activity">Activity</option>
                          <option value="TrendingUp">Trending Up</option>
                          <option value="TrendingDown">Trending Down</option>
                          <option value="Users">Users</option>
                          <option value="Sparkles">Sparkles</option>
                          <option value="CheckCircle">Check Circle</option>
                          <option value="Shield">Shield</option>
                          <option value="Layers">Layers</option>
                          <option value="Clock">Clock</option>
                          <option value="Phone">Phone</option>
                          <option value="Server">Server</option>
                          <option value="AlertTriangle">Alert Triangle</option>
                        </select>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeMetric(index)}
                      className="w-full text-red-400 border-red-500/30 hover:bg-red-500/10"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove Metric
                    </Button>
                  </div>
                ))}
                {formData.activityMetrics.length === 0 && (
                  <p className="text-neutral-400 text-sm text-center py-4">
                    No metrics yet. Click "Add Metric" to create one.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Connections */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-white">Connections</h3>
            <div className="space-y-2">
              {customer.processes && customer.processes.length > 0 && (
                <div className="text-sm">
                  <span className="text-neutral-400">Processes:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {customer.processes.map((processId) => (
                      <Badge key={processId} variant="outline" className="text-primary-400 border-primary-500/30">
                        Process #{processId.slice(0, 8)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {customer.suppliers && customer.suppliers.length > 0 && (
                <div className="text-sm">
                  <span className="text-neutral-400">Suppliers:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {customer.suppliers.map((supplierId) => (
                      <Badge key={supplierId} variant="outline" className="text-primary-400 border-primary-500/30">
                        Supplier #{supplierId.slice(0, 8)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {customer.staff && customer.staff.length > 0 && (
                <div className="text-sm">
                  <span className="text-neutral-400">Staff:</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {customer.staff.map((staffId) => (
                      <Badge key={staffId} variant="outline" className="text-primary-400 border-primary-500/30">
                        Staff #{staffId.slice(0, 8)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {(!customer.processes || customer.processes.length === 0) &&
               (!customer.suppliers || customer.suppliers.length === 0) &&
               (!customer.staff || customer.staff.length === 0) && (
                <p className="text-neutral-400 text-sm">No connections defined</p>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
